import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link, useNavigate } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import { articles } from '@/mocks/articles';
import { useState } from 'react';

const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'JMComic',
  url: 'https://drawgates.github.io',
  description: 'JMComic 是深受用户喜爱的漫画阅读平台，提供海量优质漫画资源与流畅阅读体验。',
  sameAs: ['https://drawgates.github.io/jmcomic', 'https://weixin.qq.com/jmcomic'],
};

const websiteJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'JMComic',
  url: 'https://drawgates.github.io',
  potentialAction: {
    '@type': 'SearchAction',
    'target': 'https://drawgates.github.io/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'JMComic 的最新入口地址是什么？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '请收藏本官方网站，我们会在这里及时更新最新入口地址。为确保访问稳定，建议同时关注官方社交媒体账号获取最新动态。',
      },
    },
    {
      '@type': 'Question',
      name: '为什么有时候打不开 JMComic？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '可能是网络波动或浏览器缓存问题。请尝试清除浏览器缓存和 Cookie、切换网络环境、使用最新版 Chrome 或 Edge 浏览器访问。',
      },
    },
    {
      '@type': 'Question',
      name: 'JMComic 支持多设备使用吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '支持，JMComic 支持多设备同时登录，阅读进度和书架数据会自动同步。',
      },
    },
    {
      '@type': 'Question',
      name: '漫画更新频率是怎样的？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '不同漫画的更新频率不同，热门连载漫画通常每周更新 1-2 话。已完结漫画可以一次性阅读全部章节。',
      },
    },
  ],
};

export default function Home() {
  const navigate = useNavigate();
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const hotComics = comics.slice(0, 6);
  const faqItems = [
    { q: 'JMComic 的最新入口地址是什么？', a: '请收藏本官方网站，我们会在这里及时更新最新入口地址。为确保访问稳定，建议同时关注官方社交媒体账号获取最新动态。' },
    { q: '为什么有时候打不开 JMComic？', a: '可能是网络波动或浏览器缓存问题。请尝试清除浏览器缓存和 Cookie、切换网络环境、使用最新版 Chrome 或 Edge 浏览器访问。' },
    { q: 'JMComic 支持多设备使用吗？', a: '支持，JMComic 支持多设备同时登录，阅读进度和书架数据会自动同步。' },
    { q: '漫画更新频率是怎样的？', a: '不同漫画的更新频率不同，热门连载漫画通常每周更新 1-2 话。已完结漫画可以一次性阅读全部章节。' },
  ];

  const features = [
    { icon: 'ri-book-open-line', title: '海量漫画资源', desc: '涵盖热血、恋爱、奇幻、搞笑等多种题材，持续更新中' },
    { icon: 'ri-smartphone-line', title: '多端适配', desc: '电脑、手机、平板全面适配，随时随地畅享阅读' },
    { icon: 'ri-speed-up-line', title: '流畅阅读体验', desc: '高清画质、快速加载、智能阅读模式' },
    { icon: 'ri-refresh-line', title: '跨设备同步', desc: '阅读进度、书架收藏自动同步，多设备无缝切换' },
    { icon: 'ri-download-2-line', title: '离线缓存', desc: '支持漫画章节离线下载，没网也能看' },
    { icon: 'ri-team-line', title: '社区互动', desc: '热门漫画讨论区，与同好交流剧情' },
  ];

  const homeJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'JMComic – 官方入口与完整使用指南',
    description: 'JMComic 是深受用户喜爱的漫画阅读平台，提供最新访问入口、功能介绍、常见问题解答及使用技巧。',
    url: 'https://drawgates.github.io',
    isPartOf: { '@type': 'WebSite', name: 'JMComic', url: 'https://drawgates.github.io' },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(homeJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      <SEO
        title="JMComic – 官方入口与完整使用指南 | 海量漫画在线阅读平台"
        description="JMComic 是深受用户喜爱的漫画阅读平台，本页提供 JMComic 最新访问入口、功能介绍、常见问题解答及使用技巧，帮助用户快速找到并流畅使用 JMComic，获得最佳漫画阅读体验。"
        keywords="jmcomic,jmcomic入口,jmcomic最新地址,jmcomic使用教程,jmcomic漫画资源,jmcomic访问方法,jmcomic官网导航"
        canonicalUrl="https://drawgates.github.io"
        ogImage="https://readdy.ai/api/search-image?query=A%20vibrant%20colorful%20manga%20reading%20platform%20brand%20banner%20with%20Japanese%20comic%20style%20elements%2C%20dynamic%20composition%2C%20bright%20and%20energetic%20colors%2C%20modern%20web%20design%20aesthetic%2C%20featuring%20manga%20characters%20and%20reading%20theme&width=1200&height=630&seq=jmcomic-og-home&orientation=landscape"
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=A%20dynamic%20and%20artistic%20illustration%20featuring%20colorful%20manga%20panels%20and%20comic%20book%20elements%20floating%20in%20a%20vibrant%20abstract%20composition%2C%20warm%20energetic%20colors%20with%20orange%20pink%20and%20gold%20tones%2C%20creative%20and%20playful%20design%2C%20modern%20graphic%20art%20style%2C%20smooth%20gradients%2C%20no%20text%20in%20the%20image&width=1600&height=900&seq=jmcomic-hero-bg&orientation=landscape"
                alt="JMComic 漫画阅读平台"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50" />
            </div>

            <div className="relative z-10 w-full px-4 md:px-6 text-center max-w-3xl mx-auto">
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 font-[var(--font-heading)] leading-tight">
                JMComic – 你的漫画阅读伙伴
              </h1>
              <p className="text-base md:text-lg text-white/85 mb-8 max-w-xl mx-auto leading-relaxed">
                JMComic 是深受用户喜爱的漫画阅读平台。提供最新 JMComic 入口、海量漫画资源与流畅阅读体验，随时随地畅享精彩漫画世界。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/all"
                  className="px-8 py-3 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap text-sm"
                >
                  立即探索漫画
                </Link>
                <Link
                  to="/article/article-1"
                  className="px-8 py-3 bg-white/15 text-white backdrop-blur-sm font-medium rounded-full hover:bg-white/25 transition-colors whitespace-nowrap text-sm"
                >
                  新手指南
                </Link>
              </div>
            </div>
          </section>

          {/* Hot Comics Section */}
          <section className="py-14 md:py-20 px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-[var(--font-heading)] mb-3">
                  热门漫画推荐
                </h2>
                <p className="text-foreground-600 text-sm md:text-base max-w-lg mx-auto">
                  精选平台最受欢迎的漫画作品，涵盖多种题材，总有一部适合你
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                {hotComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/category/${comic.category}`}
                    className="group"
                  >
                    <div className="relative overflow-hidden rounded-lg mb-2 aspect-[2/3]">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 right-2 bg-primary-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                        {comic.rating}
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors truncate">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{comic.latestChapter}</p>
                  </Link>
                ))}
              </div>

              <div className="text-center mt-8">
                <Link
                  to="/category/all"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors"
                >
                  查看全部漫画 <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </section>

          {/* Category Navigation */}
          <section className="py-14 md:py-20 bg-background-100 px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-[var(--font-heading)] mb-3">
                  漫画分类浏览
                </h2>
                <p className="text-foreground-600 text-sm md:text-base max-w-lg mx-auto">
                  按题材快速找到你喜欢的漫画类型
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {categories.filter((c) => c.slug !== 'all').map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/60 hover:border-primary-300 transition-all duration-200"
                  >
                    <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-accent-100 mb-3 group-hover:bg-accent-200 transition-colors">
                      <i className={`${cat.icon} text-lg md:text-xl text-accent-600`} />
                    </div>
                    <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-1">{cat.name}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{cat.description}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Latest Articles */}
          <section className="py-14 md:py-20 px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-[var(--font-heading)] mb-2">
                    最新文章
                  </h2>
                  <p className="text-foreground-600 text-sm">使用教程 · 技巧分享 · 漫画推荐</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {articles.slice(0, 6).map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-50 rounded-xl border border-background-200/60 overflow-hidden hover:border-primary-200 transition-all duration-200"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full font-medium">
                          {article.tags[0]}
                        </span>
                        <span className="text-xs text-foreground-400">{article.publishDate}</span>
                      </div>
                      <h3 className="text-sm md:text-base font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors mb-1.5 line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2">{article.description}</p>
                      <div className="flex items-center gap-1.5 mt-3 text-xs text-foreground-400">
                        <i className="ri-time-line" />
                        <span>{article.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section className="py-14 md:py-20 bg-background-100 px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-[var(--font-heading)] mb-3">
                  为什么选择 JMComic
                </h2>
                <p className="text-foreground-600 text-sm md:text-base max-w-lg mx-auto">
                  六大核心优势，打造极致漫画阅读体验
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {features.map((feature, idx) => (
                  <div key={idx} className="bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/60">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 mb-3">
                      <i className={`${feature.icon} text-lg text-primary-600`} />
                    </div>
                    <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-1.5">{feature.title}</h3>
                    <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{feature.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="py-14 md:py-20 px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 md:gap-8 text-center">
                {[
                  { value: '10,000+', label: '漫画资源' },
                  { value: '500万+', label: '活跃用户' },
                  { value: '99.9%', label: '服务可用率' },
                  { value: '4.8', label: '用户评分' },
                ].map((stat, idx) => (
                  <div key={idx}>
                    <div className="text-2xl md:text-3xl font-bold text-accent-500 font-[var(--font-heading)]">
                      {stat.value}
                    </div>
                    <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-14 md:py-20 bg-background-100 px-4 md:px-6">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-[var(--font-heading)] mb-3">
                  常见问题
                </h2>
                <p className="text-foreground-600 text-sm">关于 JMComic 的常见疑问解答</p>
              </div>

              <div className="flex flex-col gap-3">
                {faqItems.map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-background-50 rounded-xl border border-background-200/60 overflow-hidden"
                  >
                    <button
                      className="w-full flex items-center justify-between p-4 md:p-5 text-left"
                      onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                    >
                      <span className="text-sm md:text-base font-medium text-foreground-950 pr-4">{item.q}</span>
                      <i
                        className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-200 ${
                          activeFaq === idx ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {activeFaq === idx && (
                      <div className="px-4 md:px-5 pb-4 md:pb-5">
                        <p className="text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="text-center mt-8">
                <Link
                  to="/article/article-4"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors"
                >
                  查看更多 FAQ <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-14 md:py-20 px-4 md:px-6 relative overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20abstract%20illustration%20with%20soft%20gradients%20of%20orange%20and%20coral%20tones%2C%20flowing%20organic%20shapes%2C%20modern%20and%20clean%20design%2C%20energetic%20yet%20warm%20atmosphere%2C%20no%20text&width=1600&height=600&seq=jmcomic-cta-bg&orientation=landscape"
                alt=""
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-black/30" />
            </div>
            <div className="relative z-10 max-w-lg mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-white font-[var(--font-heading)] mb-3">
                准备好开始你的漫画之旅了吗？
              </h2>
              <p className="text-white/80 text-sm md:text-base mb-6">
                立即访问 JMComic，发现属于你的精彩漫画世界
              </p>
              <Link
                to="/category/all"
                className="inline-flex px-8 py-3 bg-accent-500 text-white font-medium rounded-full hover:bg-accent-600 transition-colors whitespace-nowrap text-sm"
              >
                立即开始阅读
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}